import { forwardRef, Fragment, ReactElement, RefObject, useState } from 'react';
import type { FocusEvent as ReactFocusEvent } from 'react';

import styled from 'styled-components';

import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { MaskedInput } from '@components/masked-input';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { InputContainer } from '@components/text-field/text-field';
import { VisuallyHidden } from '@components/visually-hidden';
import { Color } from '@styles/theme';
import { formatNumber, parseLocalizedNumber, useLocaleSeparators } from '@utils/numbers';

export type NumberFieldProps = {
  error?: string;
  label?: string;
  hideLabel?: boolean;
  labelIcon?: ReactElement<IconProps>;
  isDisabled?: boolean;
  isDecimal?: boolean;
  labelColor?: Color;
  placeholder?: string;
  defaultValue?: number | string | null;
  autoFocus?: boolean;
  onChange?(newValue: number | undefined): void;
  onBlur?(event?: ReactFocusEvent): void;
  helpText?: string;
  dataTestid?: string;
};

export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>((props, ref) => {
  const {
    isDecimal = false,
    error,
    isDisabled,
    hideLabel,
    label,
    labelIcon,
    labelColor,
    placeholder,
    defaultValue,
    autoFocus,
    onChange,
    onBlur,
    helpText,
    dataTestid,
  } = props;
  const { group, decimal } = useLocaleSeparators();
  const initialValue =
    defaultValue !== undefined && defaultValue !== null && defaultValue !== ''
      ? typeof defaultValue === 'number'
        ? formatNumber(defaultValue)
        : (() => {
            const n = parseLocalizedNumber(String(defaultValue));
            return Number.isFinite(n) ? formatNumber(n) : '';
          })()
      : '';
  const [value, setValue] = useState<string>(initialValue);
  // Build a dynamic number mask function for text-mask
  const buildNumberMask = (
    isDecimal: boolean,
    useGrouping: boolean,
    groupSeparator: string | null,
    decimalSeparator: string
  ) => {
    return (inputValue: string) => {
      let value = inputValue ?? '';
      // Allow an optional leading minus; strip it for digit analysis and re-add to the mask later
      const isNegative = value.trim().startsWith('-');
      if (isNegative) {
        value = value.trim().slice(1);
      }
      // Only add fractional digit slots to the mask after the user types the decimal separator
      const hasDecimal = isDecimal && value.includes(decimalSeparator);
      // Extract the integer part as typed (strip all delimiters)
      const valueAsString = String(value);
      // Keep only digits and the locale decimal separator for mask calculations
      const sanitizedNumericString = valueAsString.replace(
        new RegExp(`[^0-9${decimalSeparator}]`, 'g'),
        ''
      );
      const [integerDigits, fractionDigits = ''] = sanitizedNumericString.split(decimalSeparator);
      const sanitizedFractionDigits = fractionDigits.replace(/[^0-9]/g, '');
      const integerDigitCount = integerDigits.length;
      const mask: Array<RegExp | string> = [];
      // When no integer digits yet: allow '-' or a digit first; ensure at least one digit slot to start typing
      if (integerDigitCount === 0) {
        if (isNegative) {
          mask.push('-', /\d/);
        } else {
          mask.push(/[-\d]/, /\d/);
        }
      } else {
        if (isNegative) {
          mask.push('-');
        }
        let remainingDigits = integerDigitCount;
        let firstGroupSize = remainingDigits % 3;
        if (firstGroupSize === 0) firstGroupSize = Math.min(3, remainingDigits);

        // Add grouping separators between thousands while preserving already typed digit count
        for (let i = 0; i < firstGroupSize; i++) {
          mask.push(/\d/);
        }
        remainingDigits -= firstGroupSize;

        while (remainingDigits > 0) {
          if (useGrouping && groupSeparator) mask.push(groupSeparator);
          for (let i = 0; i < 3; i++) {
            mask.push(/\d/);
          }
          remainingDigits -= 3;
        }
      }

      if (hasDecimal) {
        mask.push(decimalSeparator);
        // Reserve at least one fractional digit slot so the user can continue typing after the separator
        const fractionDigitsMaskLength = Math.max(1, sanitizedFractionDigits.length + 1);
        for (let i = 0; i < fractionDigitsMaskLength; i++) {
          mask.push(/\d/);
        }
      }

      return mask;
    };
  };

  const mask = buildNumberMask(isDecimal, Boolean(group), group, decimal);
  const FormLabelContainer = hideLabel ? VisuallyHidden : Fragment;

  const handleChange = (selectedValue: string) => {
    const text = selectedValue ?? '';
    setValue(text);
    if (!onChange) return;
    if (text === '') {
      return onChange(undefined);
    }
    const parsedNumber = parseLocalizedNumber(text, group, decimal);
    if (Number.isFinite(parsedNumber)) {
      onChange(parsedNumber);
    } else {
      onChange(undefined);
    }
  };

  return (
    <Stack space={1}>
      <FormLabelContainer>
        <FormLabel text={label} icon={labelIcon} color={labelColor} />
      </FormLabelContainer>
      <Stack space={1} orientation="vertical">
        <NumberFieldContainer data-error={Boolean(error)} data-disabled={isDisabled}>
          <MaskedInput
            ref={ref as RefObject<MaskedInput>}
            mask={mask}
            guide={false}
            placeholderChar=" "
            placeholder={placeholder}
            autoFocus={autoFocus}
            onTextChange={handleChange}
            value={value}
            onBlur={onBlur}
            data-testid={dataTestid}
          />
        </NumberFieldContainer>
        <FormError text={error} />
        {helpText && <Text font="regular14" color="purple80" text={helpText} />}
      </Stack>
    </Stack>
  );
});

const NumberFieldContainer = styled(InputContainer)({ flexGrow: 1 });
